import {
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import classNames from "classnames/bind";
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameWeek,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { Button } from "../../../Button";
import styles from "../DateSelector.module.scss";
import { NOW } from "../dateSelectorConstants";
import type { RangePickerProps } from "../types";

const cx = classNames.bind(styles);

const WeekPicker = ({
  value,
  onChange,
  onClose,
  isRange,
  min: minDate,
  max: maxDate,
  isClearable = false,
}: RangePickerProps) => {
  const [hovered, setHovered] = useState<Date | undefined>();
  const [min, setMin] = useState<Date | undefined>();

  const [baseValue, setBaseValue] = useState(
    value ? (value instanceof Date ? value : value?.from) : NOW
  );

  const options = useMemo(() => {
    const minDay = startOfMonth(baseValue);
    const maxDay = endOfMonth(baseValue);

    return eachWeekOfInterval({ start: minDay, end: maxDay }).reduce(
      (total: Date[], start) => {
        return [
          ...total,
          ...eachDayOfInterval({ start, end: endOfWeek(start) }),
        ];
      },
      []
    );
  }, [baseValue]);

  const middleValue = useMemo(
    () => options[Math.floor(options.length / 2)],
    [options]
  );

  const onClick = (newValue: Date) => {
    const shouldSetStart =
      !value?.from ||
      !min ||
      isBefore(newValue, min) ||
      (value?.to && isBefore(newValue, value.to)) ||
      (value instanceof Date && isBefore(newValue, value));

    if (shouldSetStart) {
      onChange({
        from: startOfWeek(newValue),
        to: isRange ? undefined : endOfWeek(newValue),
      });

      if (isRange) {
        setMin(startOfWeek(newValue));
      } else {
        onClose();
      }
    } else {
      onChange({ ...value, to: endOfWeek(newValue) });

      onClose();
    }
  };

  const onNextOptionsClick = () => {
    setBaseValue(addMonths(baseValue, 1));
  };

  const onNextYearClick = () => {
    setBaseValue(addYears(baseValue, 1));
  };

  const onPreviousOptionsClick = () => {
    setBaseValue(subMonths(baseValue, 1));
  };

  const onPreviousYearClick = () => {
    setBaseValue(subYears(baseValue, 1));
  };

  const onMouseEnterCalendarItem = (item: Date) => {
    setHovered(item);
  };

  const onMouseLeaveCalendarItem = () => {
    setHovered(undefined);
  };

  return (
    <div className={cx("yearPickerContainer")}>
      <div className={cx("yearPickerHeader")}>
        <button
          type="button"
          className={cx("caretButton")}
          onClick={onPreviousYearClick}
        >
          <CaretDoubleLeft />
        </button>
        <button
          type="button"
          className={cx("caretButton")}
          onClick={onPreviousOptionsClick}
        >
          <CaretLeft />
        </button>

        <p className={cx("decadeRange")}>
          {format(options[Math.floor(options.length / 2)], "MMM yyyy")}
        </p>

        <button
          type="button"
          className={cx("caretButton")}
          onClick={onNextOptionsClick}
        >
          <CaretRight />
        </button>

        <button
          type="button"
          className={cx("caretButton")}
          onClick={onNextYearClick}
        >
          <CaretDoubleRight />
        </button>
      </div>

      <div className={cx("dayPickerCalendar")}>
        <p className={cx("dayIndicator")}>S</p>
        <p className={cx("dayIndicator")}>M</p>
        <p className={cx("dayIndicator")}>T</p>
        <p className={cx("dayIndicator")}>W</p>
        <p className={cx("dayIndicator")}>T</p>
        <p className={cx("dayIndicator")}>F</p>
        <p className={cx("dayIndicator")}>S</p>

        {options.map((date) => (
          <button
            type="button"
            key={date.toString()}
            className={cx("calendarItem", {
              isInWindow: isWithinInterval(date, {
                start: startOfMonth(middleValue),
                end: endOfMonth(middleValue),
              }),
              isHovered: hovered && isSameWeek(date, hovered),
              isEndSelected:
                (value?.from && isSameDay(date, value.from)) ||
                (value?.to && isSameDay(date, value.to)),
              ...(isRange
                ? {
                    isSelected:
                      value?.from &&
                      (isSameWeek(date, value.from) ||
                        (value?.to &&
                          isWithinInterval(date, {
                            start: value?.from,
                            end: value?.to,
                          }))),
                  }
                : {
                    isSelected: value?.from && isSameWeek(date, value.from),
                  }),
            })}
            onClick={() => onClick(date)}
            onMouseEnter={() => onMouseEnterCalendarItem(date)}
            onMouseLeave={onMouseLeaveCalendarItem}
            disabled={
              (minDate && isBefore(date, minDate)) ||
              (maxDate && isAfter(date, maxDate))
            }
          >
            {format(date, "d")}
          </button>
        ))}
      </div>

      {isClearable && (
        <div className={cx("footer")}>
          <Button
            variant="outline"
            onClick={() => {
              onChange(undefined);
              onClose();
            }}
            className={cx("clear")}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default WeekPicker;
